import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, Btn } from "@/dashboard/ui";
import { formatApiError } from "@/lib/AuthContext";
import { ChevronDown, ChevronUp, Crown } from "lucide-react";

const gbp = (pence) => `£${(pence / 100).toFixed(pence % 100 ? 2 : 0)}`;

function PlanRow({ plan, onSaved }) {
  const [price, setPrice] = useState((plan.price_pence / 100).toFixed(0));
  const [confirming, setConfirming] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [saving, setSaving] = useState(false);

  const newPence = Math.round(parseFloat(price || "0") * 100);
  const priceChanged = Number.isFinite(newPence) && newPence !== plan.price_pence;

  const save = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/admin/plans/${plan.id}`, payload);
      toast.success("Plan updated");
      setConfirming(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleHistory = async () => {
    if (!showHistory && !history) {
      try {
        const { data } = await api.get(`/admin/plans/${plan.id}/price-history`);
        setHistory(data.history);
      } catch (e) {
        toast.error(formatApiError(e));
        return;
      }
    }
    setShowHistory(!showHistory);
  };

  return (
    <div className="rounded-2xl border border-neutral-200 p-5" data-testid={`admin-plan-${plan.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-[#111111]">
            {plan.name}
            {plan.featured && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#111111] px-2 py-0.5 text-[10px] font-semibold text-white">
                <Crown size={10} /> Featured
              </span>
            )}
            {!plan.active && (
              <span className="ml-2 rounded-full border border-neutral-300 px-2 py-0.5 text-[10px] font-semibold text-neutral-500">
                Disabled for new subscriptions
              </span>
            )}
          </p>
          <p className="mt-0.5 text-xs text-neutral-500">{plan.description}</p>
          <p className="mt-1 text-xs text-neutral-400">Current price: <span className="font-semibold text-neutral-600">{gbp(plan.price_pence)}/month</span> · price version {plan.price_version}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-neutral-600">
            £
            <input
              type="number" min="1" step="1"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              data-testid={`admin-plan-price-input-${plan.id}`}
              className="w-24 rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm outline-none focus:border-neutral-500"
            />
            /month
          </label>
          {priceChanged && !confirming && (
            <Btn data-testid={`admin-plan-review-${plan.id}`} onClick={() => setConfirming(true)}>Review change</Btn>
          )}
          <Btn
            variant="ghost"
            data-testid={`admin-plan-featured-${plan.id}`}
            disabled={plan.featured || saving}
            onClick={() => save({ featured: true })}
          >
            {plan.featured ? "Featured" : "Make featured"}
          </Btn>
          <Btn
            variant="ghost"
            data-testid={`admin-plan-toggle-${plan.id}`}
            disabled={saving}
            onClick={() => save({ active: !plan.active })}
          >
            {plan.active ? "Disable" : "Enable"}
          </Btn>
          <Btn variant="ghost" data-testid={`admin-plan-history-${plan.id}`} onClick={toggleHistory}>
            {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Price history
          </Btn>
        </div>
      </div>

      {confirming && priceChanged && (
        <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-4" data-testid={`admin-plan-confirm-${plan.id}`}>
          <p className="text-sm font-semibold text-[#111111]">
            Change {plan.name} from {gbp(plan.price_pence)} to {gbp(newPence)} per month?
          </p>
          <p className="mt-1 text-xs leading-relaxed text-neutral-500">
            Applies to new customers only. Existing subscribers keep their agreed price.
            Scheduling an increase for existing customers is <span className="font-semibold">not available until billing and notifications are ready</span> — it requires at least 45 days' notice from confirmed delivery.
          </p>
          <div className="mt-3 flex gap-2">
            <Btn data-testid={`admin-plan-confirm-save-${plan.id}`} disabled={saving} onClick={() => save({ price_pence: newPence })}>
              {saving ? "Saving…" : "Confirm — new customers only"}
            </Btn>
            <Btn variant="ghost" onClick={() => { setConfirming(false); setPrice((plan.price_pence / 100).toFixed(0)); }}>Cancel</Btn>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="mt-4 border-t border-neutral-100 pt-3" data-testid={`admin-plan-history-list-${plan.id}`}>
          {(history || []).map((h) => (
            <p key={h.id} className="text-xs text-neutral-500">
              v{h.version} — <span className="font-semibold text-neutral-700">{gbp(h.price_pence)}</span> · effective {String(h.created_at).slice(0, 10)} · {h.note}
            </p>
          ))}
          {history && history.length === 0 && <p className="text-xs text-neutral-400">No history yet.</p>}
        </div>
      )}
    </div>
  );
}

export default function SubscriptionPlansAdmin() {
  const [plans, setPlans] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = async () => {
    try {
      const [{ data: p }, { data: a }] = await Promise.all([
        api.get("/admin/plans"),
        api.get("/admin/pricing-audit"),
      ]);
      setPlans(p.plans);
      setAudit(a.entries.slice(0, 10));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Card className="p-6 lg:col-span-2" data-testid="settings-plans-card">
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-neutral-500" data-testid="admin-subscription-plans-header">Subscription plans</p>
      <p className="mb-4 text-xs leading-relaxed text-neutral-500">
        Published monthly prices. Editing a price applies to new customers only — existing subscribers keep their agreed price.
        Increases for existing customers stay disabled until billing, notifications and legal checks are ready (SK-66), and then require at least 45 days' notice from confirmed delivery.
      </p>
      <div className="space-y-3">
        {(plans || []).map((p) => <PlanRow key={`${p.id}-v${p.price_version}-${p.price_pence}`} plan={p} onSaved={load} />)}
        {plans === null && <p className="text-sm text-neutral-400">Loading plans…</p>}
      </div>
      {audit.length > 0 && (
        <div className="mt-5 border-t border-neutral-100 pt-4" data-testid="admin-pricing-audit">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-neutral-500">Pricing change log</p>
          {audit.map((e) => (
            <p key={e.id} className="text-xs text-neutral-500">
              {String(e.at).slice(0, 16).replace("T", " ")} — {e.plan_id}: {e.action}
              {e.changes?.price_pence && ` (${gbp(e.changes.price_pence.old)} → ${gbp(e.changes.price_pence.new)})`}
            </p>
          ))}
        </div>
      )}
    </Card>
  );
}
